import {
  DetailedHTMLProps,
  HTMLAttributes,
  InputHTMLAttributes,
  JSX,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import Text, { TextProps } from '@components/Text';
import InputHelperText from '@components/InputHelperText';

type HtmlInputProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export interface InputProps extends DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {
  defaultValue?: HtmlInputProps['defaultValue'];
  disabled?: HtmlInputProps['disabled'];
  endAdornment?: ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string | null;
  helperTextProps?: Omit<TextProps, 'children'>;
  id?: string;
  inputProps?: Omit<
    HtmlInputProps,
    | 'defaultValue'
    | 'disabled'
    | 'id'
    | 'max'
    | 'min'
    | 'name'
    | 'onChange'
    | 'placeholder'
    | 'readOnly'
    | 'required'
    | 'step'
    | 'type'
    | 'value'
  >;
  inputWrapperClassName?: string;
  label?: TextProps['children'];
  labelProps?: Omit<TextProps, 'children'>;
  min?: HtmlInputProps['min'];
  max?: HtmlInputProps['max'];
  maxLength?: HtmlInputProps['maxLength'];
  name?: HtmlInputProps['name'];
  onChange?: HtmlInputProps['onChange'];
  placeholder?: HtmlInputProps['placeholder'];
  readOnly?: HtmlInputProps['readOnly'];
  required?: HtmlInputProps['required'];
  startAdornment?: ReactNode;
  step?: HtmlInputProps['step'];
  type?: HtmlInputProps['type'];
  value?: HtmlInputProps['value'];
}

const defaultInputStyles =
  'webkit-none py-2.5 text-body2 text-white outline-none bg-transparent';

function Input({
  className,
  defaultValue,
  disabled,
  endAdornment,
  error,
  fullWidth = false,
  helperText,
  helperTextProps,
  id,
  inputProps,
  inputWrapperClassName,
  label,
  labelProps,
  max,
  maxLength,
  min,
  name,
  onChange,
  placeholder,
  readOnly,
  required,
  startAdornment,
  step,
  type = 'text',
  value,
  ...props
}: InputProps): JSX.Element {
  const { className: inputClassName, ...spreadableInputProps } =
    inputProps ?? {};
  const borderColor = error ? 'border-red-50' : 'border-white/10';

  return (
    <div className={clsx(fullWidth && 'w-full', className)} {...props}>
      {label && (
        <Text
          as="label"
          className="mb-2 block font-medium"
          htmlFor={id}
          size="subheading3"
          {...(labelProps ?? {})}
        >
          {label}
        </Text>
      )}
      <div className={clsx('inline-block', fullWidth && 'w-full')}>
        <div
          className={clsx(
            'flex flex-row items-center rounded-md border bg-white/10',
            borderColor,
            fullWidth && 'w-full',
            inputWrapperClassName
          )}
        >
          {!!startAdornment && (
            <div
              className={clsx(
                defaultInputStyles,
                'rounded-l-md border-r px-3',
                borderColor
              )}
            >
              {startAdornment}
            </div>
          )}
          <input
            className={clsx(
              'flex-1 px-3',
              defaultInputStyles,
              fullWidth && 'w-full',
              startAdornment && 'border-l',
              borderColor,
              inputClassName
            )}
            defaultValue={defaultValue}
            disabled={disabled}
            id={id}
            max={max}
            maxLength={maxLength}
            min={min}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            step={step}
            type={type}
            value={value}
            {...spreadableInputProps}
          />
          {!!endAdornment && (
            <div className={clsx(defaultInputStyles, 'pr-3')}>
              {endAdornment}
            </div>
          )}
        </div>
      </div>
      {helperText && (
        <InputHelperText error={error} {...(helperTextProps ?? {})}>
          {helperText}
        </InputHelperText>
      )}
    </div>
  );
}

export default Input;
